import * as React from 'react';
import { getSlotIndex } from '../components/TimelineCalendar/timelineCalendarHelperFuncitons';
import { BookedEventType, Photographer } from '../BookingsCalendarTypes';
import NewBookingEventItem from '../components/TimelineCalendar/TimelineCalendarNewBookingItem';

type useAvailableSlotsProps = {
    index: number;
    photographer: Photographer;
    newBookingLenght: number;
    businessHours: Date[];
    hideTravelTimes: boolean;
};

const updateAvailableEvent = (
    photographer,
    businessHours,
    newBookingLenght,
    index,
    setIsAvailableForDrop,
    setIsAvailableToResize
) => {
    photographer.bookedEvents.forEach((event) => {
        let eventStartIndex = Math.round(getSlotIndex(businessHours[0], new Date(event.start)));
        let eventEndIndex = Math.round(getSlotIndex(businessHours[0], new Date(event.end)));

        if (event.type === BookedEventType.available) {
            let lastAvailableSlot = eventEndIndex - newBookingLenght;

            if (index >= eventStartIndex && index <= lastAvailableSlot) {
                setIsAvailableForDrop(true);
            }

            if (index >= eventStartIndex && index <= eventEndIndex - 1) {
                setIsAvailableToResize(true);
            }
        }
    });
};

const updateFirstTravelEvent = (
    businessHours,
    firstTravelEvent,
    index,
    setIsAvailableForDrop,
    setIsAvailableToResize
) => {
    let eventStartIndex = Math.round(
        getSlotIndex(businessHours[0], new Date(firstTravelEvent.start))
    );
    let eventEndIndex = Math.round(getSlotIndex(businessHours[0], new Date(firstTravelEvent.end)));

    if (index >= eventStartIndex && index <= eventEndIndex - 1) {
        setIsAvailableForDrop(true);
        setIsAvailableToResize(true);
    }
};

const updateLastTravelEvent = (businessHours, lastTravelEvent, index, setIsAvailableToResize) => {
    let eventStartIndex =
        lastTravelEvent.length > 0 &&
        Math.round(getSlotIndex(businessHours[0], new Date(lastTravelEvent.start)));
    let eventEndIndex =
        lastTravelEvent.length > 0 &&
        Math.round(getSlotIndex(businessHours[0], new Date(lastTravelEvent.end)));

    if (index >= eventStartIndex && index <= eventEndIndex - 1) {
        setIsAvailableToResize(true);
    }
};

const updateLastAvailableEvent = (
    businessHours,
    lastAvailableEvent,
    travelEvents,
    hideTravelTimes,
    index,
    newBookingLenght,
    setIsAvailableForDrop
) => {
    let eventStartIndex =
        lastAvailableEvent &&
        Math.round(getSlotIndex(businessHours[0], new Date(lastAvailableEvent.start)));

    let eventEndIndex =
        lastAvailableEvent &&
        Math.round(getSlotIndex(businessHours[0], new Date(lastAvailableEvent.end)));

    let lastTravelEvent = travelEvents.length > 0 && travelEvents.length - 1;

    let travelEventStartIndex =
        lastTravelEvent &&
        Math.round(getSlotIndex(businessHours[0], new Date(travelEvents[lastTravelEvent].start)));
    let travelEventEndIndex =
        lastTravelEvent &&
        Math.round(getSlotIndex(businessHours[0], new Date(travelEvents[lastTravelEvent].end)));

    let diff = travelEventEndIndex - travelEventStartIndex;

    if (
        hideTravelTimes &&
        index >= eventStartIndex &&
        index <= eventEndIndex + diff - newBookingLenght
    ) {
        setIsAvailableForDrop(true);
    }

    if (
        !hideTravelTimes &&
        index >= eventEndIndex - newBookingLenght + 1 &&
        index <= eventEndIndex
    ) {
        setIsAvailableForDrop(false);
    }
};

const createSlotAvailabilityTypes = (
    photographer,
    businessHours,
    newBookingLenght,
    index,
    hideTravelTimes,
    setIsAvailableForDrop,
    setIsAvailableToResize
) => {
    if (!hideTravelTimes) {
        photographer.bookedEvents.map((event) => {
            let eventStartIndex =
                event && Math.round(getSlotIndex(businessHours[0], new Date(event.start)));
            let eventEndIndex =
                event && Math.round(getSlotIndex(businessHours[0], new Date(event.end)));

            if (event.type === BookedEventType.available) {
                let lastAvailableSlot = eventEndIndex - newBookingLenght;

                if (index >= eventStartIndex && index <= lastAvailableSlot) {
                    setIsAvailableForDrop(true);
                }

                if (index >= eventStartIndex && index <= eventEndIndex - 1) {
                    setIsAvailableToResize(true);
                }
            }

            if (event.type === BookedEventType.travel) {
                if (index >= eventStartIndex && index <= eventEndIndex - 1) {
                    setIsAvailableForDrop(false);
                }
            }
        });
    }

    if (hideTravelTimes) {
        let travelEvents = photographer.bookedEvents.filter(
            (event) => event.type === BookedEventType.travel
        );
        let availableEvents = photographer.bookedEvents.filter(
            (event) => event.type === 0 && event.orderId === null
        );

        let lastAvailableEvent = availableEvents.length > 0 && [availableEvents.length - 1];

        let firstTravelEvent = travelEvents.length > 0 && travelEvents[0];

        let lastTravelEvent = travelEvents.length > 0 && travelEvents[travelEvents.length - 1];

        updateAvailableEvent(
            photographer,
            businessHours,
            newBookingLenght,
            index,
            setIsAvailableForDrop,
            setIsAvailableToResize
        );

        if (firstTravelEvent) {
            updateFirstTravelEvent(
                businessHours,
                firstTravelEvent,
                index,
                setIsAvailableForDrop,
                setIsAvailableToResize
            );
        }

        if (lastAvailableEvent) {
            updateLastTravelEvent(businessHours, lastTravelEvent, index, setIsAvailableToResize);
        }

        if (lastAvailableEvent) {
            updateLastAvailableEvent(
                businessHours,
                lastAvailableEvent,
                travelEvents,
                hideTravelTimes,
                index,
                newBookingLenght,
                setIsAvailableForDrop
            );
        }
    }
};

function useCreateSlotType({
    index,
    photographer,
    newBookingLenght,
    businessHours,
    hideTravelTimes
}: useAvailableSlotsProps) {
    const [isAvailableForDrop, setIsAvailableForDrop] = React.useState<boolean>(false);
    const [isAvailableToResize, setIsAvailableToResize] = React.useState<boolean>(false);

    React.useEffect(() => {
        createSlotAvailabilityTypes(
            photographer,
            businessHours,
            newBookingLenght,
            index,
            hideTravelTimes,
            setIsAvailableForDrop,
            setIsAvailableToResize
        );
    }, [photographer, businessHours, newBookingLenght, index, hideTravelTimes]);

    return {
        isAvailableToResize,
        isAvailableForDrop
    };
}

export default useCreateSlotType;
